import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { CalendarClock, Save } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface ConsultationSettings {
  enabled: boolean;
}

const ConsultationSettingsManagement = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('setting_value')
        .eq('setting_key', 'consultation_booking')
        .maybeSingle();

      if (error) throw error;

      if (data?.setting_value) {
        const value = data.setting_value as unknown as ConsultationSettings;
        setEnabled(value.enabled !== false);
      }
    } catch (error) {
      console.error('Error loading consultation settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('site_settings')
        .update({ setting_value: { enabled } as any })
        .eq('setting_key', 'consultation_booking');

      if (error) throw error;

      toast({
        title: 'Settings saved',
        description: enabled
          ? 'Book Free Consultation buttons are now visible across the site.'
          : 'Book Free Consultation buttons are now hidden across the site.',
      });
    } catch (error) {
      toast({
        title: 'Save failed',
        description: error instanceof Error ? error.message : 'Something went wrong.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-64" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarClock className="h-5 w-5" />
          Consultation Booking
        </CardTitle>
        <CardDescription>
          Show or hide all "Book Free Consultation" buttons and links across the site.
          Turning this off hides them everywhere without deleting anything.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div className="space-y-0.5">
            <Label htmlFor="consultation-toggle" className="text-base">
              Enable Book Free Consultation
            </Label>
            <p className="text-sm text-muted-foreground">
              {enabled
                ? 'Consultation booking CTAs are currently visible.'
                : 'Consultation booking CTAs are currently hidden.'}
            </p>
          </div>
          <Switch
            id="consultation-toggle"
            checked={enabled}
            onCheckedChange={setEnabled}
          />
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving}>
            <Save className="mr-2 h-4 w-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ConsultationSettingsManagement;
